import { TextField } from 'components/widgets';
import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import service from '../service';
import FieldLabel from './FieldLabel';

const WEBSITE_VALIDATE_DEBOUNCE_MS = 400;

const WebsiteField = ({ onValidityChange, translationPrefix, containerClass = '', translate }) => {
    const { values, initialValues, setFieldValue, dirty } = useFormikContext();
    const value = values.website;
    const savedWebsite = initialValues.website;

    const [touched, setTouched] = useState(false);
    const [apiWebsiteValid, setApiWebsiteValid] = useState(null);
    const validationSeq = useRef(0);

    const trimmedWebsite = value.trim();
    const savedWebsiteTrimmed = (savedWebsite || '').trim();
    const websiteUnchangedFromSaved = trimmedWebsite.length > 0 && trimmedWebsite === savedWebsiteTrimmed;

    useEffect(() => {
        if (!dirty) {
            setTouched(false);
            setApiWebsiteValid(null);
        }
    }, [dirty]);

    useEffect(() => {
        let timerId = null;

        if (trimmedWebsite === '') {
            setApiWebsiteValid(null);
        } else if (websiteUnchangedFromSaved) {
            setApiWebsiteValid(true);
        } else {
            const seq = ++validationSeq.current;
            timerId = setTimeout(() => {
                service
                    .validateWebsite(trimmedWebsite)
                    .then(res => {
                        if (seq === validationSeq.current) {
                            setApiWebsiteValid(Boolean(res && res.valid));
                        }
                    })
                    .catch(() => {
                        if (seq === validationSeq.current) {
                            setApiWebsiteValid(false);
                        }
                    });
            }, WEBSITE_VALIDATE_DEBOUNCE_MS);
        }

        return () => {
            if (timerId !== null) {
                clearTimeout(timerId);
            }
        };
    }, [trimmedWebsite, websiteUnchangedFromSaved, touched]);

    const websiteValid = trimmedWebsite.length > 0 && (websiteUnchangedFromSaved || apiWebsiteValid === true);
    const showError = touched && trimmedWebsite.length > 0 && !websiteUnchangedFromSaved && apiWebsiteValid === false;

    useEffect(() => {
        onValidityChange(websiteValid);
    }, [websiteValid, onValidityChange]);

    return (
        <>
            <FieldLabel icon="world" translationKey={`${translationPrefix}.WEBSITE_LABEL`} required />
            <TextField
                value={value}
                onChange={newValue => setFieldValue('website', newValue)}
                onBlur={() => setTouched(true)}
                error={showError ? `${translationPrefix}.MESSAGES.WEBSITE_ERROR` : null}
                inputConfig={{
                    placeholder: translate(`${translationPrefix}.WEBSITE_PLACEHOLDER`),
                }}
                containerClass={containerClass}
            />
        </>
    );
};

WebsiteField.propTypes = {
    onValidityChange: PropTypes.func.isRequired,
    translationPrefix: PropTypes.string.isRequired,
    containerClass: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

export default WebsiteField;
